import Form from 'antd/lib/form';
import Modal from 'antd/lib/modal';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppComponentProps } from '@/typescriptHelpers';
import { AppSettingsUpdatable } from '@/actions/updatable';
import { globalMessage } from '@/utils/globalMessage';
import { UpdateBundleSizeDeviceModelRequest, tryUpdateAppSettings } from '@/actions/appSettings';
import { selectBundleSizeDeviceModelId, selectASCDeviceModels } from '@/selectors/selectors';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import Select from 'antd/lib/select';
import Text from 'antd/lib/typography/Text';

interface EditBundleSizeDeviceModelModalProps extends AppComponentProps {
    open: boolean;
    onCancel: () => void;
}

const EditBundleSizeDeviceModelModal: React.FC<EditBundleSizeDeviceModelModalProps> = (
    props: EditBundleSizeDeviceModelModalProps,
) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const bundleSizeDeviceModelId = useSelector(selectBundleSizeDeviceModelId(props.appId));
    const ascDeviceModels = useSelector(selectASCDeviceModels(props.appId));

    const updateBundleSizeDeviceModel = (value: { bundleSizeDeviceModelId: string }) => {
        const request: UpdateBundleSizeDeviceModelRequest = {
            appId: props.appId,
            orgId: props.orgId,
            bundleSizeDeviceModelId: value.bundleSizeDeviceModelId ?? '',
        };
        dispatch(tryUpdateAppSettings(AppSettingsUpdatable.UpdateBundleSizeDeviceModel, request));
    };

    const isLoading = useIsLoading(AppSettingsUpdatable.UpdateBundleSizeDeviceModel);
    const success = useUpdated(AppSettingsUpdatable.UpdateBundleSizeDeviceModel);
    const error = useError(AppSettingsUpdatable.UpdateBundleSizeDeviceModel);

    React.useEffect(() => {
        if (success) {
            globalMessage.success('Bundle size device model updated.');
            props.onCancel();
        }
    }, [success]);

    React.useEffect(() => {
        if (error) {
            globalMessage.error(error);
        }
    }, [error]);

    React.useEffect(() => {
        if (props.open) {
            form.setFieldsValue({
                bundleSizeDeviceModelId: bundleSizeDeviceModelId || 'Universal',
            });
        }
    }, [props.open, bundleSizeDeviceModelId, form]);

    const options = ascDeviceModels.map((m) => ({
        value: m.id,
        label:
            m.id === 'Universal' ? m.displayName || 'Universal'
            : m.displayName ? `${m.displayName} (${m.id})`
            : m.id,
    }));

    return (
        <Modal
            title="Bundle size device model"
            open={props.open}
            onCancel={props.onCancel}
            onOk={() => form.submit()}
            okText="Save"
            destroyOnClose
            okButtonProps={{ loading: isLoading }}
        >
            <Text style={{ display: 'block', marginBottom: 16 }}>
                The device model you select here will be used when fetching bundle download and install size data from
                App Store Connect for org overview charts and bundle size alerts.
            </Text>
            <Form form={form} onFinish={updateBundleSizeDeviceModel} layout="vertical">
                <Form.Item name="bundleSizeDeviceModelId" noStyle>
                    <Select
                        showSearch
                        style={{ width: '100%' }}
                        placeholder="Select device model"
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                            String(option?.label ?? '')
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }
                        options={options}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default EditBundleSizeDeviceModelModal;
